'use client';

import { useEffect, useRef, useState } from 'react';
import { isNonProdOrigin } from '@/lib/nonProdHost';
import {
    clearPreview,
    CORNER_HOLD_MS,
    fetchReleaseBuilds,
    isTyping,
    readPreview,
    releaseInProgress,
    sequenceMatcher,
    shortBuild,
    writePreview,
    type ReleaseBuilds,
    type ReleaseSide,
} from './releaseSides';

/**
 * Bottom-left, hidden: lets a teammate pick which side of a release in progress they see (releaseSides.ts).
 * Opens on typing `gzr` outside a text field, or, outside production, on resting the pointer in the bottom-left
 * corner. Stays visible as a small marker while a side is pinned, so nobody forgets they are on one. Mounted on
 * SPA and server-rendered pages alike; takes no props, so it can be a client island.
 */
export function ReleaseSwitcher() {
    const [open, setOpen] = useState(false);
    const [pinned, setPinned] = useState<ReleaseSide | null>(null);
    const [builds, setBuilds] = useState<ReleaseBuilds | null | 'loading'>('loading');
    const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [cornerHover, setCornerHover] = useState(false);

    useEffect(() => {
        const pin = readPreview();
        setPinned(pin);
        // A pin outlives the release it was for (the cookie lasts the session): once no release is in progress,
        // drop it, so the marker does not claim a canary that is gone and the next release's canary is not entered
        // before its gate without choosing it again.
        if (pin) {
            void fetchReleaseBuilds().then((found) => {
                if (found && !releaseInProgress(found)) {
                    clearPreview();
                    setPinned(null);
                }
            });
        }
        setCornerHover(isNonProdOrigin(window.location.origin, window.location.hostname));
        const matches = sequenceMatcher();
        const onKey = (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
            if (matches(event.key, event.timeStamp)) setOpen(value => !value);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setBuilds('loading');
        void fetchReleaseBuilds().then((found) => {
            if (!cancelled) setBuilds(found);
        });
        return () => {
            cancelled = true;
        };
    }, [open]);

    const choose = (side: ReleaseSide) => {
        writePreview(side);
        window.location.reload();
    };

    const startHold = () => {
        holdTimer.current = setTimeout(() => setOpen(true), CORNER_HOLD_MS);
    };
    const cancelHold = () => {
        if (holdTimer.current) clearTimeout(holdTimer.current);
        holdTimer.current = null;
    };

    const found = builds === 'loading' ? null : builds;
    const inProgress = releaseInProgress(found);
    const current: ReleaseSide = pinned ?? 'stable';
    const label = (side: ReleaseSide) => {
        const name = side === 'canary' ? 'Canary' : 'Stable';
        return inProgress && found ? `${name} ${shortBuild(found[side])}` : name;
    };
    const idle = builds !== 'loading' && !inProgress
        ? `No release in progress${found ? ` (${shortBuild(found.stable)})` : ''}`
        : null;

    return (
        <div className="fixed bottom-0 left-0 z-[9998] print:hidden" data-testid="release-switcher">
            {cornerHover && !open && (
                <div className="absolute bottom-0 left-0 size-6" onMouseEnter={startHold} onMouseLeave={cancelHold} aria-hidden="true" />
            )}
            {!open && pinned && (
                <button
                    type="button"
                    className="m-2 rounded-full bg-gz-blue-800 px-3 py-1 text-xs font-bold text-white shadow"
                    onClick={() => setOpen(true)}
                    data-testid="release-switcher-marker"
                >
                    {pinned === 'canary' ? 'Canary' : 'Stable'}
                </button>
            )}
            {open && (
                <div className="m-2 flex items-center gap-2 rounded-lg bg-gz-blue-800 px-3 py-2 text-xs text-white shadow-lg" data-testid="release-switcher-panel">
                    <label htmlFor="release-switcher-select" className="font-bold">Release</label>
                    <select
                        id="release-switcher-select"
                        className="rounded bg-white px-2 py-1 text-gz-neutral-900"
                        value={current}
                        disabled={!inProgress}
                        onChange={event => choose(event.target.value as ReleaseSide)}
                        data-testid="release-switcher-select"
                    >
                        <option value="stable">{label('stable')}</option>
                        <option value="canary">{label('canary')}</option>
                    </select>
                    {builds === 'loading' && <span>Checking…</span>}
                    {idle && <span data-testid="release-switcher-idle">{idle}</span>}
                    <button type="button" className="ml-1 underline" onClick={() => setOpen(false)} aria-label="Close release switcher">
                        ✕
                    </button>
                </div>
            )}
        </div>
    );
}
